import { Box, Button, Flex, Input, Spinner, Switch, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save, Send } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import useApiRequest from "../../lib/hooks/useRequest";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/components/ui/form";

/** One value per section below. Mirrors the WebhookArea enum in the backend. */
const JOB_OFFERS_AREA = "job-offers";

interface WebhookEndpointResponse {
  area: string;
  url: string;
  enabled: boolean;
}

interface WebhookSettingsResponse {
  endpoints: WebhookEndpointResponse[];
}


interface WebhookTestEventResponse {
  success: boolean;
  statusCode?: number | null;
  errorMessage?: string | null;
}

// Spiegelt die Regeln des Backends, damit der Fehler im Formular auftaucht statt erst
// als abgelehnter Request. Die verbindliche Prüfung bleibt serverseitig.
const isPublicHttpsUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i.test(url.hostname);
  } catch {
    return false;
  }
};

const formSchema = z.object({
  url: z.union([
    z.string().refine(isPublicHttpsUrl, "Bitte eine https-Adresse mit einem echten Hostnamen angeben, zum Beispiel https://hook.eu2.make.com/…"),
    z.literal(""),
  ]),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

const Webhooks = () => {
  const { apiRequest } = useApiRequest();
  const [endpoint, setEndpoint] = useState<WebhookEndpointResponse>();
  const [isLoaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string>();
  const [testResult, setTestResult] = useState<WebhookTestEventResponse>();
  const [isSendingTest, setSendingTest] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { url: "", enabled: false },
  });

  const loadSettings = useCallback(async () => {
    setLoadError(undefined);
    const response = await apiRequest<WebhookSettingsResponse>("webhooks/settings", "GET");

    if (response?.data) {
      const jobOffers = response.data.endpoints.find((candidate) => candidate.area === JOB_OFFERS_AREA);
      setEndpoint(jobOffers);
      setLoaded(true);
      form.reset({ url: jobOffers?.url ?? "", enabled: jobOffers?.enabled ?? false });
      return;
    }

    // Without this the component would keep showing a spinner forever - the endpoint is
    // superadmin-only, so a 403 is a realistic outcome.
    setLoadError(
      response?.status === 403 ? "Für diesen Bereich fehlen dir die Rechte." : "Die Einstellungen konnten nicht geladen werden."
    );
    // form is deliberately not in the dependencies: reset would otherwise run on every keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRequest]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onSubmit = async (values: FormValues) => {
    const response = await apiRequest<WebhookSettingsResponse>("webhooks/settings", "PUT", {
      body: { area: JOB_OFFERS_AREA, url: values.url, enabled: values.enabled },
      toast: { toastText: "Webhook gespeichert" },
    });

    if (response?.data) {
      setTestResult(undefined);
      const jobOffers = response.data.endpoints.find((candidate) => candidate.area === JOB_OFFERS_AREA);
      setEndpoint(jobOffers);
      form.reset({ url: jobOffers?.url ?? "", enabled: jobOffers?.enabled ?? false });
    }
  };


  const sendTestEvent = async () => {
    setSendingTest(true);
    setTestResult(undefined);

    const response = await apiRequest<WebhookTestEventResponse>(`webhooks/settings/${JOB_OFFERS_AREA}/test-event`, "POST");

    setTestResult(response?.data);
    setSendingTest(false);
  };

  if (loadError) {
    return (
      <Flex className="flex-col gap-3">
        <Text className="text-red-600 text-sm">{loadError}</Text>
        <Button type="button" variant="outline" colorScheme="primary" className="w-fit" onClick={loadSettings}>
          Erneut versuchen
        </Button>
      </Flex>
    );
  }

  if (!isLoaded) {
    return <Spinner />;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full flex flex-col gap-8 h-full bg-white">
        <Flex className="h-full flex-col">
          <Text className="text-gray-400 text-sm mb-6">
            Schickt Ereignisse aus kandidat.io an einen Empfänger, zum Beispiel ein Make-Szenario. Interner Bereich – für
            Kunden gibt es hier nichts einzustellen.
          </Text>

          <Text className="font-bold text-md mb-1">Stellenanzeigen</Text>
          <Text className="text-gray-400 text-sm mb-4">
            Meldet, sobald eine Stelle aktiviert oder deaktiviert wird – mit Orga-ID, Stellen-ID, Standort-ID und
            Zeitstempel. Eine umbenannte Stelle bleibt über ihre ID erkennbar.
          </Text>

          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem className="flex gap-4 mb-4 w-full grow">
                <FormLabel className="max-w-52 w-full font-bold mt-4 text-md">Webhook-URL</FormLabel>
                <Box className="w-full">
                  <FormControl>
                    <Input placeholder="https://hook.eu2.make.com/..." autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                  <Text className="text-gray-400 text-xs mt-1">
                    Leer lassen ist in Ordnung. Ohne URL geht nichts raus. Nur https-Adressen mit öffentlichem Hostnamen.
                  </Text>
                </Box>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="enabled"
            render={({ field }) => (
              <FormItem className="flex gap-4 mb-4 w-full grow">
                <FormLabel className="max-w-52 w-full font-bold mt-4 text-md">Webhook aktiv</FormLabel>
                <Box className="w-full mt-3">
                  <FormControl>
                    <Switch isChecked={field.value} onChange={(event) => field.onChange(event.target.checked)} />
                  </FormControl>
                  <FormMessage />
                  <Text className="text-gray-400 text-xs mt-1">
                    Solange das aus ist, wird kein Ereignis verschickt.
                  </Text>
                </Box>
              </FormItem>
            )}
          />


          <Box className="mt-6 pt-6 border-t border-gray-200">
            <Text className="font-bold text-md mb-2">Verbindung prüfen</Text>
            <Text className="text-gray-400 text-sm mb-3">
              Schickt ein Test-Ereignis an die gespeicherte URL – unabhängig davon, ob der Webhook aktiv ist. So lässt
              sich ein Empfänger prüfen, bevor er scharfgeschaltet wird.
            </Text>
            <Button
              type="button"
              variant="outline"
              colorScheme="primary"
              className="flex gap-2 p-2 w-fit"
              isDisabled={!endpoint?.url || isSendingTest}
              isLoading={isSendingTest}
              onClick={sendTestEvent}
            >
              <Box boxSize={4} display="flex" alignItems="center" justifyContent="center">
                <Send size={16} />
              </Box>
              <span>Test-Ereignis senden</span>
            </Button>
            {!endpoint?.url && (
              <Text className="text-gray-400 text-xs mt-2">Erst eine URL speichern, dann lässt sich testen.</Text>
            )}
            {testResult && (
              <Text className={`text-sm mt-3 ${testResult.success ? "text-green-600" : "text-red-600"}`}>
                {testResult.success
                  ? `Der Empfänger hat angenommen (HTTP ${testResult.statusCode ?? 200}).`
                  : `Der Empfänger hat abgelehnt${testResult.statusCode ? ` (HTTP ${testResult.statusCode})` : ""}: ${
                      testResult.errorMessage ?? "unbekannter Fehler"
                    }`}
              </Text>
            )}
          </Box>
        </Flex>

        <Flex className="gap-4 justify-end mt-auto">
          <Button
            colorScheme="primary"
            color="white"
            className="flex gap-2 p-2 text-white ml-auto mt-4"
            variant="solid"
            type="submit"
          >
            <Box boxSize={5} display="flex" alignItems="center" justifyContent="center">
              <Save />
            </Box>
            <span>Speichern</span>
          </Button>
        </Flex>
      </form>
    </Form>
  );
};

export default Webhooks;
